import { Dialog, IconButton, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { CarDownloadButton } from "@app/components/Buttons";
import { useChartActiveElement } from "@app/components/ChartTooltip";
import { CarClose } from "@app/components/Icons";
import { ChartDftTotalReturns } from "@app/components/ChartDftTotalReturns";
import { ChartDftTotalReturnsSummary } from "@app/pages/assetAllocation/editPortfolioGroup/ChartDftTotalReturnsSummary";
import { ChartDftVersusTenYearReturn } from "@app/components/ChartDftVersusTenYearReturn";
import { useState } from "react";
import { assetClassAnalysisPages } from "@app/const/route.const";
import { openInNewTab } from "@app/utils";
import { CarAssetClassDftItem } from "./useAssetClassSummaryStat";
import { ChartDftVersusTenYearReturnBoxAndWhisker } from "@app/components/ChartDftVersusTenYearReturnBoxAndWhisker";
import {
  DftChartKind,
  useDftChartKindSelector,
} from "./useDftChartKindSelector";
import { CarSelectOptionField } from "@app/components/Inputs";

export interface DftChartDialogProps {
  items: CarAssetClassDftItem[];
  assetClassId: string;
  onApply: (value: boolean) => void;
  onCancel: () => void;
}

export const DftChartDialog = (props: DftChartDialogProps) => {
  const [assetClassId, setAssetClassId] = useState(props.assetClassId);
  const currentItem = props.items.find((i) => i.level4Id === assetClassId);

  const dftChartKindSelector = useDftChartKindSelector({
    storageKey: "DftChartDialog_dftChartKindSelector",
  });

  const currentItemIndex = props.items.findIndex(
    (i) => i.level4Id === assetClassId,
  );
  const prevId =
    currentItemIndex > 0
      ? props.items.at(currentItemIndex - 1)?.level4Id
      : undefined;
  const nextId = props.items.at(currentItemIndex + 1)?.level4Id;

  const activeElement = useChartActiveElement();

  const handleShowPdf = () => {
    openInNewTab(
      assetClassAnalysisPages.getPdfDistanceFromTrendReportUrl({
        assetClass1Id: props.assetClassId,
        assetClass2Id: "",
        isCompare: false,
      }),
    );
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          borderRadius: "15px",
          position: "relative",
          width: "1200px",
          height: "95vh",
          p: 7,
          pt: 8,
        },
      }}
      maxWidth="xl"
      open={true}
      onClose={(e, reason) => props.onCancel()}
    >
      <IconButton
        sx={{
          position: "absolute",
          right: 10,
          top: 10,
          color: "gray6",
        }}
        onClick={props.onCancel}
      >
        <CarClose />
      </IconButton>
      <Box sx={{ display: "flex" }}>
        <Typography variant="h2SSemiBold">Distance From Trend</Typography>
        <CarDownloadButton
          onClick={handleShowPdf}
          sx={{ ml: "auto", mr: -1, alignSelf: "center" }}
        >
          Download Reports
        </CarDownloadButton>
      </Box>
      {currentItem && (
        <>
          <ChartDftTotalReturnsSummary
            sx={{ mt: 5 }}
            dftItem={currentItem}
            navInfo={{
              canGoPrev: !!prevId,
              goPrev: () => setAssetClassId(prevId ?? ""),
              canGoNext: !!nextId,
              goNext: () => setAssetClassId(nextId ?? ""),
            }}
            isWhiteContext
          />
          <Box
            sx={{
              mt: 6.5,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <ChartDftTotalReturns
              dftItem={currentItem}
              pendoPrefix="asset_class_analysis_dft_total_returns"
              activeElement={activeElement}
              isWhiteContext={true}
            />
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Typography variant="h6SemiBold">Chart Selection -</Typography>
              <CarSelectOptionField
                sx={{ width: 180 }}
                {...dftChartKindSelector}
              />
            </Box>
            {dftChartKindSelector.value === DftChartKind.indexPoints ? (
              <ChartDftVersusTenYearReturn
                dftItem={currentItem}
                pendoPrefix="asset_class_analysis_dft_versus_ten_year_return"
                activeElement={activeElement}
                isWhiteContext={true}
              />
            ) : (
              <ChartDftVersusTenYearReturnBoxAndWhisker
                dftItem={currentItem}
                isWhiteContext
              />
            )}
          </Box>
        </>
      )}
    </Dialog>
  );
};
